import { useSearchParams } from 'next/navigation';
import { PropsWithChildren } from 'react';
import { Provider } from 'react-redux';

import { useStore } from '@/hooks/store/useStore';
import { AppInitialState } from '@/store';
import { RouteSpecificPurchaseParams } from '@/store/purchaseParams/types';
import { initPurchaseParams, PurchaseQueryParams } from '@/utils/getInitialState';

const StoreProvider = ({
	children,
	initialState,
	routeSpecificPurchaseParams = {},
}: PropsWithChildren<{
	initialState: AppInitialState;
	routeSpecificPurchaseParams?: RouteSpecificPurchaseParams;
}>) => {
	const searchParams = useSearchParams();

	const queryParams: PurchaseQueryParams = {
		plan: searchParams?.get('plan'),
		trialLength: searchParams?.get('trialLength'),
		utm_medium: searchParams?.get('utm_medium'),
		coupon: searchParams?.get('coupon'),
		promotion: searchParams?.get('promotion'),
		guestPass: searchParams?.get('guestPass'),
		calmjwt: searchParams?.get('calmjwt'),
		is30DayOverride: searchParams?.get('is30DayOverride'),
	};

	// while we migrate to app router, this will support both
	// SSR and app route based instantiation
	const initialPurchaseParams =
		initialState?.purchaseParams ??
		initPurchaseParams({
			query: queryParams,
			prices: initialState?.prices ?? null,
			user: initialState?.user ?? null,
			routeSpecificPurchaseParams: routeSpecificPurchaseParams ?? undefined,
		});

	const stateWithPurchaseParams: AppInitialState = {
		...initialState,
		purchaseParams: initialPurchaseParams,
	};

	const store = useStore(stateWithPurchaseParams);

	return <Provider store={store}>{children}</Provider>;
};

export default StoreProvider;
